import * as React from 'react';
import { Text, VStack } from '@ayble-health/dashboard-components';
import { PillToggleGroup } from '../../ui/pill-toggle-group';
import { FilterSection } from './FilterSection';
import {
  PAST_VISIT_TIMING_OPTIONS,
  UPCOMING_VISIT_TIMING_OPTIONS,
  VISIT_CONFIRMATION_OPTIONS,
} from '../constants';
import { RosterFilters } from './types';

/**
 * When the appointment is, and whether it's confirmed.
 *
 * Titled "Appointments" to match the roster's own column header — this was
 * "Visit Status" until round 6, which meant the sidebar and the table used two
 * different names for one thing.
 *
 * **Timing renders as two labelled rows driving one value.** Upcoming and past
 * are different questions, and the grouping is what tells a user they're
 * alternatives rather than things to combine. It is presentation only: both
 * `PillToggleGroup`s read and write the same single `visitTiming` scalar, so
 * selecting "Last Week" clears "This Week" for free.
 *
 * "None Scheduled" sits in the Upcoming row rather than on its own: it answers
 * the upcoming question too — "upcoming: none" — so it belongs beside the
 * windows it's the absence of.
 *
 * That single-select is also the whole enforcement of "a past window and a
 * future window can't both be active" (round 7). No disabled states are used
 * for it, deliberately: under single-select *every* other pill is equally
 * exclusive with the current selection, so greying out only the future ones
 * would imply None Scheduled was still combinable. The grouping states the
 * distinction honestly; a disabled state would misstate the model.
 *
 * Note the past windows are the only place a past appointment surfaces at all.
 * By default the roster shows upcoming appointments only — see
 * `displayedAppointment`.
 *
 * Everything selectable here is single-select. Timing always was — the buckets
 * overlap, so picking two says nothing. The confirmation pills became so in
 * round 6: it's a two-value boolean, so the only multi-selection available
 * (both pills) already meant the same thing as selecting neither, which made
 * the control offer a choice that did nothing.
 *
 * The brief's separate "Today's visits only" toggle lives here as the Today
 * pill instead of sitting in the header, so there is exactly one control per
 * question and no way for the two to disagree.
 *
 * Note: these filters are attendee-blind. A member on the selected care team's
 * panel matches on their appointment whoever it's with — a coach or an RD visit
 * still counts. Who it's with decides where the member *ranks*, not whether
 * they appear; see `matchesVisitStatus` and the two-tier sort in
 * `useHealthCoachDashboard`.
 */
const GroupLabel = ({ children }: { children: React.ReactNode }) => (
  <Text my={0} fontSize="xs" color="muted.500">
    {children}
  </Text>
);

export const AppointmentsSection = ({ filters }: { filters: RosterFilters }) => {
  // Both rows are one control. Each hands the group its own slice of the
  // selection and gets back either an empty array or a single value, which the
  // hook narrows to the scalar.
  const selected = filters.visitTiming ? [filters.visitTiming] : [];

  return (
    <FilterSection title="Appointments">
      <VStack gap={3} align="stretch">
        <VStack gap={2} align="stretch">
          <GroupLabel>Upcoming</GroupLabel>
          <PillToggleGroup
            options={UPCOMING_VISIT_TIMING_OPTIONS}
            selected={selected}
            onChange={filters.onVisitTimingChange}
            multiple={false}
          />
        </VStack>

        <VStack gap={2} align="stretch">
          <GroupLabel>Past</GroupLabel>
          <PillToggleGroup
            options={PAST_VISIT_TIMING_OPTIONS}
            selected={selected}
            onChange={filters.onVisitTimingChange}
            multiple={false}
          />
        </VStack>

        <VStack gap={2} align="stretch">
          <GroupLabel>Confirmations</GroupLabel>
          <PillToggleGroup
            options={VISIT_CONFIRMATION_OPTIONS}
            selected={filters.selected.visitConfirmations}
            onChange={(v) => filters.setFilter('visitConfirmations', v)}
            multiple={false}
          />
        </VStack>
      </VStack>
    </FilterSection>
  );
};
